import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { listPulledPayers } from '../../services/api';
import type {
  KinnserPullSummary,
  PulledClinicPayers,
} from '../../services/api/types.gen';

/**
 * The last pull's payers, each with the payer id and support Admin's insurance
 * table will show once a run has written it -- so a payer that matched nothing,
 * or cannot run eligibility, is seen before Run rather than after.
 *
 * Matched by the server with the sync's own lookup, on every new pull.
 */

const Tick: React.FC<{ on: boolean; label: string }> = ({ on, label }) =>
  on ? (
    <Check
      aria-label={`${label}: supported`}
      className='mx-auto h-3.5 w-3.5 text-green-600'
    />
  ) : (
    <span className='sr-only'>{`${label}: not supported`}</span>
  );

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

export const PulledPayers: React.FC<{
  agencyId: string;
  pull: KinnserPullSummary | null | undefined;
  excluded: ReadonlySet<string>;
}> = ({ agencyId, pull, excluded }) => {
  const [clinics, setClinics] = useState<PulledClinicPayers[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Keyed on the pull, so a new pull is matched again.
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    listPulledPayers({ path: { agency_id: agencyId }, throwOnError: true })
      .then(res => {
        if (!cancelled) setClinics(res.data?.clinics ?? []);
      })
      .catch(caught => {
        console.error('Failed to load the pulled payers', caught);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId, pull?.pulled_at]);

  if (failed) {
    return (
      <p className='border-t pt-2 text-xs text-muted-foreground'>
        Unable to load the payers. Please try again.
      </p>
    );
  }
  if (clinics === null) return null;

  const shown = clinics.filter(
    clinic => !excluded.has(clinic.clinic_id) && clinic.payers.length > 0
  );
  if (shown.length === 0) {
    // A pull saved before payers were stored carries counts but no list; any
    // other empty answer means no built clinic has payers, which a pull won't fix.
    const stale = (pull?.clinic_list ?? []).some(
      clinic =>
        !excluded.has(clinic.clinic_id) &&
        (clinic.insurances ?? 0) > 0 &&
        !clinic.insurance_list?.length
    );
    return (
      <p className='border-t pt-2 text-xs text-muted-foreground'>
        {stale
          ? 'Pull again to see their names.'
          : 'No clinic this sandbox builds has payers.'}
      </p>
    );
  }

  // Counted once per payer, not once per clinic that carries it: the line
  // answers "how many payers need fixing", and Admin lists each payer once.
  const payers = [
    ...new Map(
      shown
        .flatMap(clinic => clinic.payers)
        .map(payer => [payer.payer_id || payer.name, payer])
    ).values(),
  ];
  const missingId = payers.filter(payer => !payer.payer_id).length;
  const unknownId = payers.filter(
    payer => payer.payer_id && !payer.matched_payer_id
  ).length;
  const eligible = payers.filter(payer => payer.eligibility).length;

  return (
    <div className='space-y-3 border-t pt-2'>
      {/* The answer first: how many will work, and why the rest will not. */}
      <p className='text-xs'>
        {eligible} of {plural(payers.length, 'payer', 'payers')} can run
        eligibility checks.
        {missingId > 0 && (
          <span className='text-amber-700'>
            {' '}
            {plural(missingId, 'has', 'have')} no payer ID in Kinnser.
          </span>
        )}
        {unknownId > 0 && (
          <span className='text-amber-700'>
            {' '}
            {plural(unknownId, 'payer ID is', 'payer IDs are')} not in Roger’s
            payer list.
          </span>
        )}
        {(missingId > 0 || unknownId > 0) &&
          ' Set them in Admin → Insurance after Run.'}
      </p>

      {/* One table per clinic, stacked, with fixed column widths so every
          clinic's columns line up with the others. Each keeps
          its own headings, so a column is never read against the wrong one. */}
      <div className='max-h-[32rem] space-y-4 overflow-auto'>
        {shown.map(clinic => (
          <table
            key={clinic.clinic_id}
            className='w-full min-w-[36rem] table-fixed text-xs'
          >
            <caption className='mb-1 text-left text-xs font-medium'>
              {clinic.name}
            </caption>
            <thead className='text-muted-foreground'>
              <tr className='border-b'>
                <th className='py-1 text-left font-normal'>Payer</th>
                <th className='w-40 py-1 text-left font-normal'>Payer ID</th>
                <th className='w-20 py-1 text-center font-normal'>
                  Eligibility
                </th>
                <th className='w-16 py-1 text-center font-normal'>Claims</th>
                <th className='w-16 py-1 text-center font-normal'>ERA</th>
              </tr>
            </thead>
            <tbody>
              {clinic.payers.map((payer, index) => (
                <tr key={index} className='border-b border-border/50'>
                  <td className='truncate py-1' title={payer.name}>
                    {payer.name}
                  </td>
                  <td className='py-1'>
                    {payer.matched_payer_id ? (
                      payer.matched_payer_id
                    ) : payer.payer_id ? (
                      <span className='text-amber-700'>
                        {payer.payer_id} (unknown)
                      </span>
                    ) : (
                      <span className='text-muted-foreground'>None</span>
                    )}
                  </td>
                  <td className='py-1 text-center'>
                    <Tick on={!!payer.eligibility} label='Eligibility' />
                  </td>
                  <td className='py-1 text-center'>
                    <Tick on={!!payer.claims} label='Claims' />
                  </td>
                  <td className='py-1 text-center'>
                    <Tick on={!!payer.era} label='ERA' />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
};

export default PulledPayers;
